import React, { useState, useEffect, useRef, useMemo } from "react";
import "../css/campaign.scss";
import { useNavigate } from "react-router-dom";
import QuickShareModal from "./QuickShareModal";
import { getFullDonationUrl } from "../utils/urlUtils";
import { BASE_URL, LIST_URL } from "../constans/ApiEndpointConstants";
import { PixelWithEvent } from "./common/PixelWithEvent ";
import { sendMessage } from "../apis/SendMessageAPI";
import LocalStorageUtil from "../utils/localStorageUtil";
import * as FacebookAPI from "../apis/FacebookAPI";
import Cookies from "js-cookie";
import { getOrderList } from "../apis/GetOrderList";
import { donateDeepColor, shareColor } from "../constans/ColorConstants";
import { reactions } from "../Datas/Reactions";
import {
  campaignData,
  enrichDonationsWithRandomTime,
} from "../Datas/campaignData";

// Simple Linear Congruential Generator for seeded randomness
class SeededRandom {
  constructor(seed) {
    // Ensure seed is a number and non-zero
    this.seed = (Number(seed) || 123456789) % 2147483647;
    if (this.seed <= 0) this.seed += 2147483646;
  }

  next() {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }
}

const CampaignPage = ({ detailsData }) => {
  const [videoPlayingIndex, setVideoPlayingIndex] = useState(null);
  const { donationsNewest: initialDonationsNewest } = campaignData;

  const {
    title,
    name: creator,
    post_story: story,
    raised,
    goal,
    organizeList: organizer,
    createTime,
    postType,
    image,
    supportList,
    updateList,
    avatar,
  } = detailsData;

  // 给图片加上前缀
  const resolveUrl = (raw) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
    return `${LIST_URL.replace(/\/$/, "")}/${String(raw).replace(/^\//, "")}`;
  };

  //组织者
  const organizerObj = (() => {
    if (Array.isArray(organizer) && organizer.length > 0) {
      const firstSection = organizer[0];
      const firstItem = firstSection?.list?.[0];
      if (firstItem) {
        return {
          name: firstItem.name || "",
          role: firstItem.role || "",
          location: firstItem.address || "",
          img: resolveUrl(firstItem.avatar || ""),
        };
      }
    }

    if (
      organizer &&
      typeof organizer === "object" &&
      !Array.isArray(organizer)
    ) {
      return {
        name: organizer.name || "",
        role: organizer.role || "",
        location: organizer.location || "",
        img: resolveUrl(organizer.img || ""),
      };
    }

    // fallback to empty fields
    return { name: "", role: "", location: "", img: "" };
  })();

  //支持者
  const supportItems = Array.isArray(supportList)
    ? supportList
    : Array.isArray(supportList?.list)
    ? supportList.list
    : [];

  //故事更新
  const updateItems = Array.isArray(updateList)
    ? updateList
    : Array.isArray(updateList?.list)
    ? updateList.list
    : [];

  // helper: 将可能的字符串/格式金额解析为数字（例如 "200000.00", "$200K", "200,000"）
  const parseNumeric = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    if (typeof v === "string") {
      // 去掉千位分隔符和货币符号后提取首个数字
      const cleaned = v.replace(/[,¥$€£\s]/g, "");
      const m = cleaned.match(/-?\d+(\.\d+)?/);
      return m ? Number(m[0]) : 0;
    }
    return 0;
  };

  //有效的故事更新（值不能为空）
  const visibleUpdates = updateItems.filter((u) => {
    if (!u) return false;
    const hasText =
      (u.text && String(u.text).trim().length > 0) ||
      (u.content && String(u.content).trim().length > 0);
    const hasName = u.name && String(u.name).trim().length > 0;
    const hasTime = u.time && String(u.time).trim().length > 0;
    const hasRole = u.role && String(u.role).trim().length > 0;
    const hasImages =
      (Array.isArray(u.imgList) && u.imgList.length > 0) ||
      (Array.isArray(u.images) && u.images.length > 0);
    return hasText || hasName || hasTime || hasRole || hasImages;
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);
  const [isDonationsModalOpen, setIsDonationsModalOpen] = useState(false);
  const [donationsView, setDonationsView] = useState("newest");
  const [isQuickShareModalOpen, setIsQuickShareModalOpen] = useState(false);
  const [isGuaranteeModalOpen, setIsGuaranteeModalOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerImages, setImageViewerImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isReactionsModalOpen, setIsReactionsModalOpen] = useState(false);
  const [supportMessagesToShow, setSupportMessagesToShow] = useState(5);

  //主页轮博图片（memoized，避免在多个 effect 中触发不必要的重新计算）
  const HERO_SLIDES = useMemo(() => {
    // If backend provides an `image` array, map it to slides
    if (Array.isArray(image) && image.length > 0) {
      const imageSlides = image
        .map((img) => {
          const raw = img?.url || img?.path || img?.src || "";
          if (!raw) return null;

          const lower = String(raw).toLowerCase();
          const looksLikeYoutube = /youtu\.be|youtube\.com/.test(lower);
          // detect common video file extensions (mp4/mov/webm/etc)
          const isVideoFile = /\.(mp4|mov|webm|m4v|ogg|ogv)(\?.*)?$/.test(
            lower
          );

          // handle video/link types (youtube, external video link, or direct video file)
          if (
            img?.type === "link" ||
            img?.type === "video" ||
            looksLikeYoutube ||
            isVideoFile
          ) {
            // detect youtube id and build embed src; otherwise keep raw as url
            const m = String(raw).match(
              /(?:youtu\.be\/|embed\/|v=|\/watch\?v=)([A-Za-z0-9_-]{11})/
            );
            const id = m ? m[1] : null;
            const embedSrc = id ? `https://www.youtube.com/embed/${id}` : raw;
            return {
              // treat explicit video type or direct video file as "video", otherwise "link"
              type: img?.type === "video" || isVideoFile ? "video" : "link",
              url: raw,
              src: embedSrc,
              alt: img?.alt || "Hero video",
            };
          }

          const src =
            /^https?:\/\//i.test(raw) || raw.startsWith("data:")
              ? raw
              : `${LIST_URL.replace(/\/$/, "")}/${raw.replace(/^\//, "")}`;
          return {
            type: "image",
            src,
            alt: img?.alt || "Main image",
          };
        })
        .filter(Boolean);

      // Place the story slide at third position (index 2) if there are at least 2 images,
      // otherwise append the story slide at the end.
      if (imageSlides.length >= 2) {
        return [
          ...imageSlides.slice(0, 2),
          { type: "story" },
          ...imageSlides.slice(2),
        ];
      }

      return [...imageSlides, { type: "story" }];
    }
    return [];
  }, [image]);

  // 从 YouTube embed URL 提取 video id（用于海报图）
  const getYouTubeIdFromEmbedUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    // 支持多种 YouTube URL：youtu.be/ID, /watch?v=ID, embed/ID, v=ID
    const m = url.match(
      /(?:youtu\.be\/|embed\/|v=|\/watch\?v=)([A-Za-z0-9_-]{11})/
    );
    return m ? m[1] : null;
  };

  const DONATION_SPOTLIGHT = {
    message: "Each gift brings hope — together, we create change.",
    image: process.env.PUBLIC_URL + "/main/main3-1.png",
  };

  const formatTime = (timeValue, type = "hours") => {
    if (typeof timeValue === "string") {
      // 已经有单位，直接返回
      return timeValue;
    }

    if (type === "hours") {
      if (timeValue < 1) {
        const mins = Math.round(timeValue * 60);
        return `${mins} ${mins === 1 ? "min" : "mins"}`;
      } else {
        const hrs = Math.round(timeValue);
        return `${hrs} ${hrs === 1 ? "hr" : "hrs"}`;
      }
    }

    if (type === "days") {
      return `${timeValue} d`;
    }

    return timeValue;
  };

  // 计算并返回用于显示的时间字符串，支持多种来源和回退顺序
  const computeDonationTime = (donor) => {
    if (!donor) return "";

    // 优先处理数值型 hoursAgo
    if (typeof donor.hoursAgo === "number" && Number.isFinite(donor.hoursAgo)) {
      if (donor.hoursAgo < 24) {
        return formatTime(donor.hoursAgo, "hours");
      }
      // 如果有 daysAgo（可能是数字或字符串），优先使用它
      if (
        typeof donor.daysAgo === "number" ||
        typeof donor.daysAgo === "string"
      ) {
        return formatTime(donor.daysAgo, "days");
      }
      // 回退：把小时转换为天数显示
      return formatTime(donor.hoursAgo / 24, "days");
    }

    // 如果 daysAgo 存在（数字或字符串），尝试解析并规范化单位（例如 "6 days" -> "6 d"）
    if (typeof donor.daysAgo === "number") {
      return formatTime(donor.daysAgo, "days");
    }

    if (typeof donor.daysAgo === "string") {
      const m = donor.daysAgo.match(/(\d+(?:\.\d+)?)(?:\s*(\w+))?/);
      if (m) {
        const val = parseFloat(m[1]);
        const unit = (m[2] || "").toLowerCase();
        // 保持自然单位显示：天/小时/分钟
        if (unit.startsWith("d")) {
          return `${val} d`;
        }
        if (unit.startsWith("min")) {
          return `${Math.round(val)} mins`;
        }
        if (unit.startsWith("hr") || unit.startsWith("hour")) {
          // 不再把小时转换为天，直接显示为小时
          return `${Math.round(val)} hrs`;
        }
        // 如果没有单位，按天数显示（回退）
        return formatTime(val, "days");
      }
      // 无法解析时回退到原始字符串
      return donor.daysAgo;
    }

    // 如果存在预格式化的 time 字段，尝试规范化单位（例如 "days" -> "d"）
    if (donor.time) {
      if (typeof donor.time === "string") {
        const m = donor.time.match(/(\d+(?:\.\d+)?)\s*(\w+)/);
        if (m) {
          const val = parseFloat(m[1]);
          const unit = m[2].toLowerCase();
          if (unit.startsWith("d")) {
            return `${val} d`;
          }
          if (unit.startsWith("hr") || unit.startsWith("hour")) {
            return `${Math.round(val)} hrs`;
          }
          if (unit.startsWith("min")) {
            return `${Math.round(val)} mins`;
          }
        }
        // 无法解析时返回原始字符串
        return donor.time;
      }
      return String(donor.time);
    }

    // 如果有 createtime（时间戳或可解析日期），格式化显示
    if (donor.createtime) {
      return formatCreateTimeDisplay(donor.createtime);
    }

    return "";
  };

  const dedupeDonations = (list) => {
    const uniqueMap = new Map();
    list.forEach((donation) => {
      if (!donation) {
        return;
      }
      const key = `${donation.name}-${donation.amount}-${donation.daysAgo}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, donation);
      }
    });
    return Array.from(uniqueMap.values());
  };

  // Fisher-Yates 洗牌，用于每次更新时随机化捐款顺序
  // Modified to support seeded randomness
  const shuffleArray = (arr, seed = null) => {
    const rng = seed ? new SeededRandom(seed) : null;
    const random = rng ? () => rng.next() : Math.random;

    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  };

  // 将一组 donations 按比例缩放，使它们的金额总和等于 target（保留两位小数）
  const scaleDonationsToTarget = (donations = [], target = 0) => {
    const tgt = Number(target) || 0;
    if (!Array.isArray(donations) || donations.length === 0) return donations;

    // Initialize SeededRandom with target as seed
    // This ensures the distribution is deterministic for a given target amount
    const rng = new SeededRandom(tgt);
    const random = () => rng.next();

    // User defined tiers
    const tier1 = [20, 25, 30, 35, 40, 45, 50, 55]; // Frequent
    const tier2 = [100, 150, 200]; // General
    const tier3 = [500, 1000, 1500, 2000, 5000, 10000]; // Rare (Added 500)

    const count = donations.length;
    // Helper to get random item from array using seeded random
    const getRandom = (arr) => arr[Math.floor(random() * arr.length)];

    // Initialize amounts array
    let currentAmounts = new Array(count).fill(0);
    let assignedIndices = new Set();
    let currentSum = 0;

    // --- Forced Assignment Logic ---
    // Rule: For every 5000 in target:
    // - 1 donation >= 500
    // - 2 donations > 100

    const multiplier = Math.floor(tgt / 5000);

    if (multiplier > 0) {
      const requiredHigh = 1 * multiplier; // >= 500
      const requiredMid = 2 * multiplier; // > 100

      const assignToSlot = (val) => {
        // Find an available slot
        let slot = -1;
        let attempts = 0;
        while (attempts < 100) {
          const r = Math.floor(random() * count);
          if (!assignedIndices.has(r)) {
            slot = r;
            break;
          }
          attempts++;
        }

        // Fallback: Linear scan if random failed
        if (slot === -1) {
          for (let j = 0; j < count; j++) {
            if (!assignedIndices.has(j)) {
              slot = j;
              break;
            }
          }
        }

        // If we found a slot, assign value
        if (slot !== -1) {
          currentAmounts[slot] = val;
          assignedIndices.add(slot);
          currentSum += val;
          return true;
        }
        return false;
      };

      // 1. Assign High (>= 500)
      // Candidates: Tier 3 (all are >= 500)
      // We must ensure that picking a high value doesn't make it impossible to fit other forced donations
      // Min cost for one Mid (>100) is ~150 (from Tier 2: 100, 150, 200 -> >100 are 150, 200)
      const minMidCost = 150;
      const minHighCost = 500;

      for (let k = 0; k < requiredHigh; k++) {
        // Calculate remaining budget constraint
        // We need to reserve space for:
        // - Remaining Highs: (requiredHigh - 1 - k) * minHighCost
        // - All Mids: requiredMid * minMidCost
        const reserved =
          requiredMid * minMidCost + (requiredHigh - 1 - k) * minHighCost;
        const maxAllowed = Math.max(minHighCost, tgt - currentSum - reserved);

        const candidates = tier3.filter((v) => v <= maxAllowed);

        if (candidates.length > 0) {
          const val = getRandom(candidates);
          assignToSlot(val);
        }
      }

      // 2. Assign Mid (> 100)
      // Candidates: Filter Tier 2 for > 100 (150, 200)
      const baseMidCandidates = tier2.filter((v) => v > 100);

      for (let k = 0; k < requiredMid; k++) {
        // Reserve for remaining Mids
        const reserved = (requiredMid - 1 - k) * minMidCost;
        const maxAllowed = Math.max(minMidCost, tgt - currentSum - reserved);

        const candidates = baseMidCandidates.filter((v) => v <= maxAllowed);

        if (candidates.length > 0) {
          const val = getRandom(candidates);
          assignToSlot(val);
        }
      }
    }

    // --- Fill Remaining ---
    // Fill everyone else with Tier 1 initially
    for (let i = 0; i < count; i++) {
      if (!assignedIndices.has(i)) {
        const val = getRandom(tier1);
        currentAmounts[i] = val;
        currentSum += val;
      }
    }

    let diff = tgt - currentSum;

    // Handle Overshoot
    if (diff < 0) {
      // Identify indices that were NOT forced (Tier 1s)
      const tier1Indices = [];
      for (let i = 0; i < count; i++) {
        if (!assignedIndices.has(i)) tier1Indices.push(i);
      }

      // Shuffle them to remove randomly
      for (let i = tier1Indices.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [tier1Indices[i], tier1Indices[j]] = [tier1Indices[j], tier1Indices[i]];
      }

      while (diff < 0 && tier1Indices.length > 0) {
        const idxToRemove = tier1Indices.pop();
        const val = currentAmounts[idxToRemove];
        currentAmounts[idxToRemove] = 0; // Mark for removal
        currentSum -= val;
        diff += val;
      }
    }

    // Handle Undershoot (Surplus)
    let safety = 0;
    const maxIterations = count * 50;

    while (diff >= 5 && safety < maxIterations) {
      safety++;
      const idx = Math.floor(random() * count);

      // Skip if this slot was removed (0)
      if (currentAmounts[idx] === 0) continue;

      const currentVal = currentAmounts[idx];
      let potentialUpgrade = null;
      const r = random();

      if (diff >= 1000 && r < 0.15) {
        const candidates = tier3.filter(
          (v) => v - currentVal <= diff && v > currentVal
        );
        if (candidates.length > 0) potentialUpgrade = getRandom(candidates);
      }

      if (!potentialUpgrade && diff >= 100 && r < 0.35) {
        const candidates = tier2.filter(
          (v) => v - currentVal <= diff && v > currentVal
        );
        if (candidates.length > 0) potentialUpgrade = getRandom(candidates);
      }

      if (!potentialUpgrade) {
        const candidates = tier1.filter(
          (v) => v - currentVal <= diff && v > currentVal
        );
        if (candidates.length > 0) potentialUpgrade = getRandom(candidates);
      }

      if (potentialUpgrade) {
        const cost = potentialUpgrade - currentVal;
        currentAmounts[idx] = potentialUpgrade;
        diff -= cost;
      } else {
        if (diff < 5) break;
      }
    }

    // Map result and shuffle
    const finalDonations = donations
      .map((d, i) => ({ ...d, amount: currentAmounts[i] }))
      .filter((d) => d.amount > 0);

    for (let i = finalDonations.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [finalDonations[i], finalDonations[j]] = [
        finalDonations[j],
        finalDonations[i],
      ];
    }

    return finalDonations;
  };

  // 本地（动态）捐款列表 —— 用于显示并参与随机化/轮训
  // 限制轮训池大小，避免对数千条记录做全量轮训
  const computeRotationSampleSize = (goalValue) => {
    const g = parseNumeric(goalValue);
    // fallback
    if (!g || g <= 0) return 25;

    //根据goal限制捐款列表总数
    const bands = [
      { gMin: 0, gMax: 999, sizeMin: 20, sizeMax: 50 },
      { gMin: 1000, gMax: 4999, sizeMin: 150, sizeMax: 250 },
      { gMin: 5000, gMax: 19999, sizeMin: 250, sizeMax: 400 },
      { gMin: 20000, gMax: 99999, sizeMin: 400, sizeMax: 800 },
      {
        gMin: 10000,
        gMax: Number.POSITIVE_INFINITY,
        sizeMin: 2000,
      },
    ];

    const band = bands.find((b) => g >= b.gMin && g <= b.gMax) || bands[0];

    if (!isFinite(band.gMax)) {
      return Math.round((band.sizeMin + band.sizeMax) / 2);
    }

    const t = Math.min(
      Math.max((g - band.gMin) / (band.gMax - band.gMin), 0),
      1
    );
    const size = Math.round(band.sizeMin + t * (band.sizeMax - band.sizeMin));
    return size;
  };

  const initialDynamicFull = enrichDonationsWithRandomTime(
    initialDonationsNewest || [],
    createTime
  );
  const initialSampleSize = computeRotationSampleSize(goal);

  // Use raised amount as seed for deterministic shuffle
  const seed = parseNumeric(raised);

  let initialDynamicSampled = dedupeDonations(
    shuffleArray([...initialDynamicFull], seed)
  ).slice(0, initialSampleSize);
  // 将初始样本按 raised 缩放，使金额总和等于 raised（不包括 apiDonationsTotal）
  initialDynamicSampled = scaleDonationsToTarget(
    initialDynamicSampled,
    parseNumeric(raised)
  );

  const [dynamicDonations, setDynamicDonations] = useState(
    () => initialDynamicSampled
  );

  // API 拉取的捐款（固定显示在最上面，不参与随机化）
  const [apiDonationsList, setApiDonationsList] = useState([]);

  // 存储从 API 获取的捐款总额
  const [apiDonationsTotal, setApiDonationsTotal] = useState(0);

  // 最终用于渲染的捐款列表：API 列表固定在顶部，后面跟动态列表
  const donationsNewest = useMemo(
    () => [...apiDonationsList, ...dynamicDonations],
    [apiDonationsList, dynamicDonations]
  );

  // 过滤后的最近捐赠者（用于右侧展示）
  // Modify: Prioritize showing donations >= 20. Strict filter: No amounts < 20.
  const recentDonors = useMemo(() => {
    // Strictly filter out anything < 20
    const validDonors = donationsNewest.filter((d) => Number(d.amount) >= 20);
    return validDonors.slice(0, 5);
  }, [donationsNewest]);

  // 动态计算捐款总额：初始值 + API 捐款总额（确保都是数字）
  const totalRaised = useMemo(() => {
    const base = parseNumeric(raised);
    const apiTotal = parseNumeric(apiDonationsTotal);
    return base + apiTotal;
  }, [raised, apiDonationsTotal]);

  // 计算过去24小时内的捐款人数，计算最近捐款人数
  const donationsLast24Count = useMemo(() => {
    if (!Array.isArray(donationsNewest) || donationsNewest.length === 0)
      return 0;
    const now = Date.now();
    let count = 0;
    for (const d of donationsNewest) {
      if (!d) continue;

      // 如果存在 createtime（时间戳），优先使用它计算
      if (d.createtime) {
        const tsRaw = Number(d.createtime);
        if (!Number.isNaN(tsRaw)) {
          let ts = tsRaw;
          if (ts < 10000000000) ts = ts * 1000; // 秒级 => 毫秒
          const hoursDiff = (now - ts) / (1000 * 60 * 60);
          if (hoursDiff < 24) {
            count++;
            continue;
          } else {
            continue;
          }
        }
      }

      // 支持不同数据源的字段：hoursAgo（数字）、daysAgo（数字或字符串）
      if (typeof d.hoursAgo === "number") {
        if (d.hoursAgo < 24) {
          count++;
          continue;
        }
      }

      if (typeof d.daysAgo === "number") {
        if (d.daysAgo < 1) {
          count++;
          continue;
        }
      }

      if (typeof d.daysAgo === "string") {
        // 常见格式："5 mins", "3 hrs", "2 days"
        const m = d.daysAgo.match(/(\d+(?:\.\d+)?)\s*(\w+)/);
        if (m) {
          const val = parseFloat(m[1]);
          const unit = m[2].toLowerCase();
          if (unit.startsWith("min")) {
            count++;
            continue;
          }
          if (unit.startsWith("hr") || unit.startsWith("hour")) {
            if (val < 24) {
              count++;
              continue;
            }
          }
          if (unit.startsWith("d") || unit.startsWith("day")) {
            if (val < 1) {
              count++;
              continue;
            }
          }
        }
      }
    }
    return count;
  }, [donationsNewest]);

  const formatShortCount = (n) => {
    const num = Number(n) || 0;
    if (num >= 1000) {
      const v = num / 1000;
      return v % 1 === 0 ? `${v}K` : `${v.toFixed(1)}K`;
    }
    return `${num}`;
  };

  // 动态计算捐款数量
  const donationsCount = donationsNewest.length;

  // 动态计算进度百分比
  const progressPercentage = useMemo(() => {
    const numericGoal = parseNumeric(goal);
    if (!numericGoal || numericGoal <= 0) return 0;
    const percentage = Math.round((totalRaised / numericGoal) * 100);
    return Math.min(percentage, 100); // 最大100%
  }, [totalRaised, goal]);

  // 排序显示Top捐助者
  const donationsTop = useMemo(() => {
    if (!donationsNewest?.length) {
      return [];
    }
    return [...donationsNewest].sort(
      (a, b) => (b.amount ?? 0) - (a.amount ?? 0)
    );
  }, [donationsNewest]);
  const progressGradientSeed = useMemo(
    () => Math.random().toString(36).slice(2, 11),
    []
  );
  const progressGradientId = `campaignProgressGradient-${progressGradientSeed}`;
  const progressGradientShowId = `campaignProgressGradientShow-${progressGradientSeed}`;
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [isHeroViewerActive, setIsHeroViewerActive] = useState(false);
  const [isStickyFooterVisible, setIsStickyFooterVisible] = useState(false);
  const [currentTopDonationIndex, setCurrentTopDonationIndex] = useState(0);
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const stickyTriggerRef = useRef(null);
  const storySectionRef = useRef(null);
  const carouselRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);

  const isAnyModalOpen =
    isUpdatesModalOpen ||
    isDonationsModalOpen ||
    isQuickShareModalOpen ||
    isGuaranteeModalOpen ||
    isReactionsModalOpen ||
    imageViewerOpen;

  // 解析HTML字符串为段落
  const parseStoryParagraphs = (htmlString) => {
    if (!htmlString) return [];

    // 创建一个临时的DOM元素来解析HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // 提取所有p标签的内容
    const paragraphs = Array.from(tempDiv.querySelectorAll("p"))
      .map((p) => p.innerHTML.trim())
      .filter((content) => content.length > 0);

    return paragraphs;
  };

  const storyParagraphs = parseStoryParagraphs(story);

  // 默认显示前2个段落；如果第一段过短则主动包含第二段以丰富预览
  const initialParagraphsCount = 2;
  const MIN_FIRST_PARAGRAPH_LENGTH = 80; // 小于此长度认为第一段过短
  const firstPara = storyParagraphs[0] || "";
  const shouldIncludeSecondPara =
    !isExpanded &&
    firstPara &&
    firstPara.trim().length > 0 &&
    firstPara.trim().length < MIN_FIRST_PARAGRAPH_LENGTH &&
    storyParagraphs.length > 1;

  const displayedParagraphs = isExpanded
    ? storyParagraphs
    : shouldIncludeSecondPara
    ? storyParagraphs.slice(0, 2)
    : storyParagraphs.slice(0, initialParagraphsCount);

  const hasMoreContent = storyParagraphs.length > displayedParagraphs.length;

  // 为预览创建纯文本版本（移除HTML标签）
  // 控制故事预览的固定字符数
  const PREVIEW_CHAR_LIMIT = 220;

  const createTextPreview = (html) => {
    if (!html) return "";
    // 移除 <br>，并将连续空白规范为单个空格，确保字符计算一致
    const tempDiv = document.createElement("div");
    const cleanedHtml = String(html).replace(/<br\s*\/?>/gi, " ");
    tempDiv.innerHTML = cleanedHtml;
    let text = tempDiv.textContent || tempDiv.innerText || "";
    text = text.replace(/\s+/g, " ").trim();
    return text.length > PREVIEW_CHAR_LIMIT
      ? `${text.slice(0, PREVIEW_CHAR_LIMIT).trim()}...`
      : text;
  };

  // 当展开时使用完整的 displayedParagraphs 来生成预览，避免展开后顶部显示回短的第一段
  const storyPreview = createTextPreview(displayedParagraphs.join(" "));
  const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return `$${num.toLocaleString("en", { maximumFractionDigits: 0 })}`;
  };
  const formatCurrencyTwoDecimals = (amount) => {
    const num = Number(amount) || 0;
    return `$${num.toLocaleString("en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const topDonationsCount = donationsTop?.length ?? 0;
  const topDonationSpotlight =
    topDonationsCount > 0 ? donationsTop[currentTopDonationIndex] : null;

  // 获取捐款列表（轮询 + 每次随机化顺序）
  useEffect(() => {
    let isMounted = true;

    const fetchOrderDonations = async () => {
      try {
        const response = await getOrderList(1, detailsData?.id);
        // response.data.data 才是订单数组
        const orders = response?.data?.data ?? [];

        // 仍然保留基于返回数据的标准化流程，但不强制按时间排序——我们会随机化顺序
        const normalized = normalizeDonationsFromOrders(orders, {
          raised,
          goal,
          createTime,
          apiDonationsTotal: apiDonationsTotal,
        });

        if (!isMounted) return;

        if (Array.isArray(normalized) && normalized.length) {
          // 计算 API 捐款总额
          const apiTotal = normalized.reduce(
            (sum, donation) => sum + (donation.amount ?? 0),
            0
          );
          setApiDonationsTotal(apiTotal);

          // 标记 API 来自的数据（以便它们固定在顶部且不参与随机化）
          const normalizedMarked = normalized.map((d) => ({
            ...d,
            _isApi: true,
          }));

          // 更新 api 列表（固定在顶部）
          setApiDonationsList((prevApi) => {
            // 如果内容无变化，则保持当前
            const prevJson = JSON.stringify(
              prevApi.map((p) => p.name + "|" + p.amount + "|" + p.daysAgo)
            );
            const newJson = JSON.stringify(
              normalizedMarked.map(
                (p) => p.name + "|" + p.amount + "|" + p.daysAgo
              )
            );
            if (prevJson === newJson) return prevApi;
            return normalizedMarked;
          });

          // 保持动态列表不包含 API 数据，然后对动态部分随机化
          setDynamicDonations((prevDynamic) => {
            const dynamicOnly = prevDynamic.filter((d) => !d?._isApi);

            // 计算样本大小，避免对数千条记录做全量轮训
            const sampleSize = computeRotationSampleSize(goal);

            // 随机化并取样
            // Use raised as seed for deterministic shuffle
            const seed = parseNumeric(raised);
            const shuffledDynamic = shuffleArray([...dynamicOnly], seed);
            let sampled = dedupeDonations(shuffledDynamic).slice(0, sampleSize);

            // 如果采样结果较少，尝试从 normalized 中补充（非 API 标记的）
            if (sampled.length < sampleSize) {
              const needed = sampleSize - sampled.length;
              const additional = normalized
                .filter((d) => !d?._isApi)
                .slice(0, needed)
                .map((d) => ({
                  name: d.name,
                  amount: d.amount,
                  daysAgo: d.daysAgo,
                }));
              sampled = dedupeDonations([...sampled, ...additional]).slice(
                0,
                sampleSize
              );
            }

            // 将采样结果按 raised 缩放，使金额总和等于 raised（不包括 apiDonationsTotal）
            sampled = scaleDonationsToTarget(sampled, parseNumeric(raised));

            return sampled;
          });
        }
      } catch (error) {
        console.error("Failed to fetch order list", error);
      }
    };

    // 立即拉取一次（仅在页面加载/刷新时请求一次）
    fetchOrderDonations();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!topDonationsCount) {
      return undefined;
    }

    // reset index whenever donations list changes
    setCurrentTopDonationIndex(0);

    const intervalId = setInterval(() => {
      setCurrentTopDonationIndex((prevIndex) =>
        prevIndex + 1 < topDonationsCount ? prevIndex + 1 : 0
      );
    }, 4000);

    return () => clearInterval(intervalId);
  }, [topDonationsCount]);

  // 自动轮播功能
  useEffect(() => {
    if (isAutoPlayPaused || isAnyModalOpen || isHeroViewerActive) {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
      return undefined;
    }

    autoPlayIntervalRef.current = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 4000); // 每4秒切换一次

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
    };
  }, [
    isAutoPlayPaused,
    isAnyModalOpen,
    isHeroViewerActive,
    HERO_SLIDES.length,
  ]);

  // 本地动态捐款列表轮换（仅在本地，不请求后端），每 8s 将第一条移动到末尾，暂停于 modal 打开或自动播放暂停
  useEffect(() => {
    if (!dynamicDonations || dynamicDonations.length <= 1) return undefined;

    let rotateInterval = null;

    const startRotate = () => {
      if (rotateInterval) return;
      rotateInterval = setInterval(() => {
        setDynamicDonations((prev) => {
          if (!prev || prev.length <= 1) return prev;
          const [first, ...rest] = prev;
          return [...rest, first];
        });
      }, 8000);
    };

    const stopRotate = () => {
      if (rotateInterval) {
        clearInterval(rotateInterval);
        rotateInterval = null;
      }
    };

    // 只在没有模态窗且未暂停自动播放时轮换
    if (!isAnyModalOpen && !isAutoPlayPaused) {
      startRotate();
    }

    // 监听暂停/模态变化：当这些变为 true 时停止轮换
    return () => {
      stopRotate();
    };
    // 仅依赖这些变量以便在条件变化时重启/停止轮换
  }, [dynamicDonations, isAnyModalOpen, isAutoPlayPaused]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
    };
  }, []);

  const formatGoal = (amount) => {
    const num = parseNumeric(amount);
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(0)}K`;
    }
    return `$${num}`;
  };

  const navigate = useNavigate();

  //计算时间戳
  const calculateDaysAgo = (timestamp) => {
    if (!timestamp) return "0 mins";

    let ts = Number(timestamp);
    if (Number.isNaN(ts)) return "0 mins";

    // 秒级时间戳转毫秒
    if (ts < 10000000000) ts *= 1000;

    const parsedDate = new Date(ts);
    if (Number.isNaN(parsedDate.getTime())) return "0 mins";

    const diffMs = Date.now() - parsedDate.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} mins`;
    } else if (diffHours < 24) {
      return `${diffHours} hrs`;
    } else {
      return `${diffDays} days`;
    }
  };

  // 格式化 createTime 字段为相对时间并统一单位（days -> d）
  const formatCreateTimeDisplay = (value) => {
    if (!value && value !== 0) return "";

    // 如果是字符串并且像 "13 hrs" / "2 days" 等，规范化单位
    if (typeof value === "string") {
      const m = value.match(/(\d+(?:\.\d+)?)\s*(\w+)/);
      if (m) {
        const num = parseFloat(m[1]);
        const unit = m[2].toLowerCase();
        if (unit.startsWith("d")) return `${num} d`;
        if (unit.startsWith("hr") || unit.startsWith("hour"))
          return `${Math.round(num)} hrs`;
        if (unit.startsWith("min")) return `${Math.round(num)} mins`;
        return value;
      }

      // 如果是可解析的日期字符串，转为时间戳继续处理
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) {
        value = parsed;
      } else {
        return value;
      }
    }

    // 处理数值型（秒或毫秒）
    if (typeof value === "number") {
      let ts = value;
      if (ts < 10000000000) ts = ts * 1000; // 秒级转毫秒
      const diffMs = Date.now() - ts;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffMins < 60) return `${diffMins} mins`;
      if (diffHours < 24) return `${diffHours} hrs`;
      return `${diffDays} d`;
    }

    return String(value);
  };

  // 格式化 createTime 为可读日期，例如 "Dec 28, 2026"
  const formatCreateDateDisplay = (value) => {
    if (!value && value !== 0) return "";

    let ts = value;

    // 处理字符串形式的时间（ISO 字符串或数字字符串）
    if (typeof value === "string") {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) {
        ts = parsed;
      } else {
        const num = Number(value);
        if (!Number.isNaN(num)) {
          ts = num;
        } else {
          return value;
        }
      }
    }

    // 如果是秒级时间戳，转为毫秒
    if (typeof ts === "number" && ts < 10000000000) ts = ts * 1000;

    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";

    // 返回简短可读日期：例如 "Dec 28, 2026"
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const normalizeDonationsFromOrders = (orders, campaignData = {}) => {
    const { raised, goal, apiDonationsTotal } = campaignData;
    if (!Array.isArray(orders)) {
      return [];
    }

    return orders
      .map((order) => {
        // 解析 order.data JSON 字符串
        let parsedData = {};
        if (typeof order?.data === "string") {
          try {
            parsedData = JSON.parse(order.data);
          } catch (e) {
            console.warn("Failed to parse order data:", e);
          }
        }

        // 从 parsedData 或 order 根级别获取金额（兼容不同字段名）
        const rawAmount =
          parsedData?.amount_fast ??
          parsedData?.amount ??
          parsedData?.price ??
          order?.amount ??
          order?.price ??
          0;

        // Use parseNumeric to handle currency strings like "$20.00"
        let amount = parseNumeric(rawAmount);

        // Ensure valid amount
        if (!Number.isFinite(amount) || amount <= 0) {
          return null;
        }

        // Logic that modified amounts based on goal progress has been removed
        // to ensure we display the recorded amount exactly as is.

        // 从 parsedData 获取 firstName + lastName
        const firstName = parsedData?.firstName ?? "";
        const lastName = parsedData?.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim();
        const name = fullName || "Anonymous";

        // 从 order 根级别获取 createtime —— 直接使用接口提供的时间，不做随机化/调整
        let timestamp = order?.createtime;

        const daysAgo = calculateDaysAgo(timestamp);

        return {
          name,
          amount: Math.round(amount * 100) / 100, // 保留两位小数
          daysAgo,
        };
      })
      .filter(Boolean);
  };

  //event_id，要去重，一天之内用一个，存cookie
  const getEventId = (eventType) => {
    // 根据事件类型使用不同的cookie名
    const cookieName = `fb_event_id_${eventType}`;

    let storedEventId = Cookies.get(cookieName);
    if (storedEventId) {
      return storedEventId;
    } else {
      // 使用 10 位随机数字作为 event_id
      const newEventId = Math.round(Math.random() * 10000000000).toString();
      Cookies.set(cookieName, newEventId, { expires: 1 }); // 存储1天
      return newEventId;
    }
  };

  const toCheckout = () => {
    const pixel = detailsData.pixel;
    const fbqToken = detailsData.fbq_token;

    // 生成 event_id
    const event_id = getEventId("DonateClick");

    // 构建 eventData
    const eventData = {
      content_ids: ["donation"],
      content_name: "Checkout Page",
      content_category: "Navigation",
      value: 0,
      currency: "USD",
      num_items: 0,
      contents: [],
    };

    // 调用 addevent (通过 PixelWithEvent)
    PixelWithEvent("DonateClick", event_id);

    // 调用 EventsAll (通过 FacebookAPI.Facebook)
    if (pixel && fbqToken) {
      FacebookAPI.Facebook(fbqToken, pixel, event_id, "DonateClick", eventData);
    }

    // pass full detailsData to checkout via location state
    navigate("/checkout", { state: { detailsData } });
  };

  // 处理点击事件，阻止冒泡并调用 toCheckout
  const handleCheckoutClick = (e) => {
    e.stopPropagation();
    toCheckout();
  };

  const handleOpenShareModal = () => {
    // 生成 event_id
    const event_id = getEventId("clickShare");

    // 调用 PixelWithEvent
    PixelWithEvent("Click Share", event_id);

    // 获取 ad 和 userid 并发送消息
    const storedAd = LocalStorageUtil.getAd();

    const ad = storedAd ?? "donation";
    const userid = localStorage.getItem("userid");
    const message = `${BASE_URL} [Click Share]:ad=${ad}:userid=${userid}`;
    sendMessage(message);

    setIsQuickShareModalOpen(true);
  };

  const openDonationsModal = (view = "newest") => {
    setDonationsView(view);
    setIsDonationsModalOpen(true);
  };

  const closeDonationsModal = () => setIsDonationsModalOpen(false);

  const renderDonationSpotlight = (key) => (
    <div className="campaign-page__donation-spotlight" key={key}>
      <div className="campaign-page__donation-spotlight-media">
        <img src={DONATION_SPOTLIGHT.image} alt="Hopeful child" />
      </div>
      <div className="campaign-page__donation-spotlight-content">
        <p>{DONATION_SPOTLIGHT.message}</p>
        <button
          type="button"
          className="campaign-page__donation-spotlight-cta"
          onClick={handleCheckoutClick}
        >
          Donate
        </button>
      </div>
    </div>
  );

  const currentDonations =
    (donationsView === "top" ? donationsTop : donationsNewest) || [];

  const donationRows = [];
  currentDonations.forEach((donation, index) => {
    donationRows.push(
      <div
        className="campaign-page__donations-modal-row"
        key={`${donationsView}-${donation.name}-${index}`}
      >
        <div className="campaign-page__donations-avatar campaign-page__donor-svg">
          <img alt="" src={process.env.PUBLIC_URL + "/main/handShare.png"} />
        </div>
        <div className="campaign-page__donations-row-body">
          <div className="campaign-page__donations-row-top">
            <span className="campaign-page__donations-name">
              {donation.name || "Anonymous"}
            </span>
          </div>
          <div className="campaign-page__donations-row-meta">
            <span className="campaign-page__donations-amount">
              $
              {Number(donation.amount).toLocaleString("en", {
                maximumFractionDigits: 0,
              })}
            </span>

            <span className="campaign-page__donations-separator">·</span>
            <span className="campaign-page__donations-time">
              {/* {donation.hoursAgo < 24
                ? donation.hoursAgo < 1
                  ? `${Math.round(donation.hoursAgo * 60)} mins`
                  : `${Math.round(donation.hoursAgo)} ${
                      Math.round(donation.hoursAgo) === 1 ? "hr" : "hrs"
                    }`
                : `${donation.daysAgo} d`} */}
              {/* {donation.hoursAgo < 24
                ? formatTime(donation.hoursAgo, "hours")
                : formatTime(donation.daysAgo, "days")} */}
              {computeDonationTime(donation)}
            </span>
          </div>
        </div>
      </div>
    );

    if (index === 3) {
      donationRows.push(
        renderDonationSpotlight(`donation-spotlight-mid-${donationsView}`)
      );
    }
  });

  if (currentDonations.length) {
    donationRows.push(
      renderDonationSpotlight(`donation-spotlight-end-${donationsView}`)
    );
  }

  const campaignUrl = detailsData
    ? getFullDonationUrl(detailsData, BASE_URL)
    : BASE_URL;
  const openGuaranteeModal = () => setIsGuaranteeModalOpen(true);
  const closeGuaranteeModal = () => setIsGuaranteeModalOpen(false);
  const handleGuaranteeBadgeKeyDown = (event) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault();
      openGuaranteeModal();
    }
  };

  const handlePrevHeroImage = () => {
    setCurrentHeroImage(
      (prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length
    );
    setIsAutoPlayPaused(true);
    // 3秒后恢复自动播放
    setTimeout(() => {
      setIsAutoPlayPaused(false);
    }, 3000);
  };

  const scrollToStorySection = () => {
    setIsExpanded(true);
    window.requestAnimationFrame(() => {
      storySectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleNextHeroImage = () => {
    setCurrentHeroImage((prev) => (prev + 1) % HERO_SLIDES.length);
    setIsAutoPlayPaused(true);
    // 3秒后恢复自动播放
    setTimeout(() => {
      setIsAutoPlayPaused(false);
    }, 3000);
  };

  const handleGoToHeroImage = (index) => {
    setCurrentHeroImage(index);
    setIsAutoPlayPaused(true);
    // 3秒后恢复自动播放
    setTimeout(() => {
      setIsAutoPlayPaused(false);
    }, 3000);
  };

  // 触摸滑动处理
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
    setIsAutoPlayPaused(true);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const distance = touchStartX - touchEndX;
    const minSwipeDistance = 50; // 最小滑动距离

    if (distance > minSwipeDistance) {
      // 向左滑动，显示下一张
      handleNextHeroImage();
    } else if (distance < -minSwipeDistance) {
      // 向右滑动，显示上一张
      handlePrevHeroImage();
    }

    // 重置触摸位置
    setTouchStartX(0);
    setTouchEndX(0);

    // 3秒后恢复自动播放
    setTimeout(() => {
      setIsAutoPlayPaused(false);
    }, 3000);
  };

  // 鼠标拖动处理（桌面端）
  const isMouseDownRef = useRef(false);

  const handleMouseDown = (e) => {
    isMouseDownRef.current = true;
    setTouchStartX(e.clientX);
    setIsAutoPlayPaused(true);
  };

  const handleMouseMove = (e) => {
    if (isMouseDownRef.current && touchStartX) {
      setTouchEndX(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (!isMouseDownRef.current) return;

    isMouseDownRef.current = false;

    if (!touchStartX || !touchEndX) {
      setTouchStartX(0);
      setTouchEndX(0);
      return;
    }

    const distance = touchStartX - touchEndX;
    const minSwipeDistance = 50; // 最小滑动距离

    if (distance > minSwipeDistance) {
      // 向左拖动，显示下一张
      handleNextHeroImage();
    } else if (distance < -minSwipeDistance) {
      // 向右拖动，显示上一张
      handlePrevHeroImage();
    }

    // 重置位置
    setTouchStartX(0);
    setTouchEndX(0);

    // 3秒后恢复自动播放
    setTimeout(() => {
      setIsAutoPlayPaused(false);
    }, 3000);
  };

  const openHeroImageViewer = () => {
    const activeSlide = HERO_SLIDES[currentHeroImage];
    if (activeSlide.type !== "image") return;

    const heroImages = HERO_SLIDES.filter((slide) => slide.type === "image");
    const currentImagePosition = heroImages.findIndex(
      (slide) => slide.src === activeSlide.src
    );

    setImageViewerImages(heroImages.map((slide) => slide.src));
    setCurrentImageIndex(
      currentImagePosition === -1 ? 0 : currentImagePosition
    );
    setIsHeroViewerActive(true);
    setImageViewerOpen(true);
  };

  // 锁定背景滚动：Updates 弹窗打开时禁用 body 滚动
  useEffect(() => {
    if (!isUpdatesModalOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isUpdatesModalOpen]);

  // 锁定背景滚动：Donations 弹窗打开时禁用 body 滚动
  useEffect(() => {
    if (!isDonationsModalOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isDonationsModalOpen]);

  // 锁定背景滚动：Giving Guarantee 弹窗打开时禁用 body 滚动
  useEffect(() => {
    if (!isGuaranteeModalOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isGuaranteeModalOpen]);

  // Toggle sticky footer when share/donate block leaves viewport
  useEffect(() => {
    const target = stickyTriggerRef.current;
    if (!target || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyFooterVisible(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!imageViewerOpen) return;

    let alive = true;

    const handleKeyDown = (e) => {
      if (!alive) return;
      if (e.key === "Escape") {
        setImageViewerOpen(false);
      } else if (e.key === "ArrowLeft" && imageViewerImages.length > 1) {
        setCurrentImageIndex(
          (prev) =>
            (prev - 1 + imageViewerImages.length) % imageViewerImages.length
        );
      } else if (e.key === "ArrowRight" && imageViewerImages.length > 1) {
        setCurrentImageIndex((prev) => (prev + 1) % imageViewerImages.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      alive = false;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [imageViewerOpen, imageViewerImages.length]);

  useEffect(() => {
    if (imageViewerOpen && isHeroViewerActive) {
      const heroImages = HERO_SLIDES.filter((slide) => slide.type === "image");
      if (heroImages.length === 0 || imageViewerImages.length === 0) return;

      const currentImageSrc =
        imageViewerImages[currentImageIndex % imageViewerImages.length];
      const correspondingIndex = HERO_SLIDES.findIndex(
        (slide) => slide.src === currentImageSrc
      );

      if (correspondingIndex !== -1) {
        setCurrentHeroImage(correspondingIndex);
      }
    }

    if (!imageViewerOpen && isHeroViewerActive) {
      setIsHeroViewerActive(false);
    }
  }, [
    imageViewerOpen,
    isHeroViewerActive,
    currentImageIndex,
    imageViewerImages,
    HERO_SLIDES,
  ]);
  // 是否当前展示的是视频幻灯片（用于调整控制条位置等样式）
  const isCurrentHeroVideo =
    HERO_SLIDES[currentHeroImage]?.type === "link" ||
    HERO_SLIDES[currentHeroImage]?.type === "video";

  return (
    <div
      className={`campaign-page${
        isStickyFooterVisible ? " campaign-page--sticky-active" : ""
      }`}
    >
      <h1 className="campaign-page__title ">{title}</h1>
      <div className="campaign-page__container">
        {/* Left Section - Main Content */}
        <div className="campaign-page__main">
          {/* Image Carousel */}
          <div className="campaign-page__image-container">
            {HERO_SLIDES[currentHeroImage]?.type === "image" && (
              <h2 className="campaign-page__title-overlay">{title}</h2>
            )}
            <div
              className="campaign-page__image-carousel"
              ref={carouselRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div
                className="campaign-page__image-track"
                style={{ transform: `translateX(-${currentHeroImage * 100}%)` }}
              >
                {HERO_SLIDES.map((slide, index) => {
                  if (slide.type === "story") {
                    return (
                      <div
                        key={`story-${index}`}
                        className="campaign-page__story-slide"
                      >
                        <div className="campaign-page__story-slide-content">
                          <h3>Story</h3>
                          <div
                            className="campaign-page__story-preview"
                            dangerouslySetInnerHTML={{ __html: storyPreview }}
                          />
                          <button
                            className="campaign-page__story-slide-button"
                            type="button"
                            onClick={scrollToStorySection}
                          >
                            Read more
                          </button>
                        </div>
                      </div>
                    );
                  }

                  if (slide.type === "link") {
                    const isPlaying = videoPlayingIndex === index;
                    const ytId = getYouTubeIdFromEmbedUrl(slide.url);
                    const poster = ytId
                      ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
                      : "";

                    return (
                      <div
                        key={`video-${index}`}
                        className="campaign-page__image-button campaign-page__video-slide"
                      >
                        {!isPlaying ? (
                          <button
                            type="button"
                            className="campaign-page__video-poster"
                            aria-label={
                              slide.alt || `Play hero video ${index + 1}`
                            }
                            onClick={() => {
                              // 点击播放：标记为正在播放，并暂停自动轮播
                              setVideoPlayingIndex(index);
                              setIsAutoPlayPaused(true);
                            }}
                          >
                            {poster ? (
                              <img
                                src={poster}
                                alt={
                                  slide.alt || `Hero video ${index + 1} poster`
                                }
                                className="campaign-page__video-poster-img"
                              />
                            ) : (
                              <div className="campaign-page__video-poster-fallback" />
                            )}
                            <span
                              className="campaign-page__video-play-button"
                              aria-hidden
                            >
                              <svg viewBox="0 0 24 24" width="64" height="64">
                                <path
                                  d="M8 5v14l11-7z"
                                  fill="rgba(255,255,255,0.9)"
                                ></path>
                              </svg>
                            </span>
                          </button>
                        ) : (
                          <div className="campaign-page__video-wrapper">
                            <iframe
                              src={`${slide.src}${
                                slide.src.includes("?") ? "&" : "?"
                              }autoplay=1`}
                              title={slide.alt || `Hero video ${index + 1}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="campaign-page__video-iframe"
                            />
                          </div>
                        )}
                      </div>
                    );
                  }

                  // 2️⃣ HTML5 video —— ✅ 你要的就是这里
                  if (slide.type === "video") {
                    return (
                      <div
                        key={`video-${index}`}
                        className="campaign-page__image-button campaign-page__video-slide"
                      >
                        <video
                          src={resolveUrl(slide.url)}
                          autoPlay
                          muted
                          controls
                          className="campaign-page__video-element"
                        />
                      </div>
                    );
                  }

                  return (
                    <button
                      key={slide.src}
                      type="button"
                      className="campaign-page__image-button"
                      onClick={openHeroImageViewer}
                    >
                      <img
                        src={slide.src}
                        alt={slide.alt || `Hero ${index + 1}`}
                        className="campaign-page__image"
                      />
                    </button>
                  );
                })}
              </div>
              <div
                className="campaign-page__carousel-controls"
                style={{ bottom: isCurrentHeroVideo ? "3.5rem" : undefined }}
              >
                <div className="campaign-page__image-dots">
                  {HERO_SLIDES.map((_, index) => (
                    <button
                      key={`dot-${index}`}
                      type="button"
                      className={`campaign-page__image-dot ${
                        currentHeroImage === index
                          ? "campaign-page__image-dot--active"
                          : ""
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                      onClick={() => handleGoToHeroImage(index)}
                    />
                  ))}
                </div>
                <div className="campaign-page__carousel-arrows">
                  <button
                    className="campaign-page__carousel-arrow"
                    aria-label="Previous slide"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevHeroImage();
                    }}
                  >
                    <svg
                      t="1763537521823"
                      class="icon"
                      viewBox="0 0 1024 1024"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      p-id="37622"
                      width="200"
                      height="200"
                    >
                      <path
                        d="M71.327 528.814967l289.705148 295.703769c9.380648 9.624195 24.707728 9.624195 34.138518 0 9.381671-9.576099 9.381671-25.222451 0-34.848693L146.677129 536.058952l786.690176 0c13.3214 0 24.142863-11.048637 24.142863-24.642237 0-13.597693-10.821463-24.645306-24.142863-24.645306L146.677129 486.771409l248.494561-253.632581c9.381671-9.603729 9.381671-25.248034 0-34.847669-4.76349-4.836145-10.939143-7.194866-17.116843-7.194866-6.1777 0-12.355399 2.405793-17.116843 7.194866L71.233879 494.01437c-9.383718 9.602705-9.383718 25.244964 0 34.849716L71.327 528.814967z"
                        p-id="37623"
                      ></path>
                    </svg>
                  </button>
                  <button
                    className="campaign-page__carousel-arrow"
                    aria-label="Next slide"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextHeroImage();
                    }}
                  >
                    <svg
                      t="1763537425143"
                      class="icon"
                      viewBox="0 0 1024 1024"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      p-id="37196"
                      width="200"
                      height="200"
                    >
                      <path
                        d="M950.378747 494.018463 660.673599 198.314695c-9.380648-9.624195-24.707728-9.624195-34.138518 0-9.381671 9.576099-9.381671 25.222451 0 34.848693l248.494561 253.612115L88.338443 486.775502c-13.3214 0-24.142863 11.047614-24.142863 24.641213 0 13.597693 10.821463 24.645306 24.142863 24.645306l786.690176 0L626.535081 789.694603c-9.381671 9.603729-9.381671 25.248034 0 34.847669 4.76349 4.836145 10.939143 7.194866 17.116843 7.194866 6.1777 0 12.355399-2.405793 17.116843-7.194866l289.704125-295.723212c9.383718-9.602705 9.383718-25.244964 0-34.849716L950.378747 494.018463z"
                        p-id="37197"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* svg */}
            <div className="hrt-hide-min-md hero-media-viewer_curvedOverlayContainer__QFY48">
              <svg
                aria-hidden="true"
                className="animated-curved-overlay_curvedOverlay__A9SJx animated-curved-overlay_convex__RocQe"
                data-chromatic="ignore"
                data-testid="curved-overlay-svg"
                preserveAspectRatio="none"
                viewBox="0 0 100 100"
              >
                <path d="M-2 100 Q50 0 106 106 L106 130 L-2 106 Z" />
              </svg>
            </div>
          </div>

          {/* Creator Info */}
          <div className="campaign-page__creator">
            <span className="campaign-page__creator-icon">
              {avatar ? (
                <img
                  src={resolveUrl(avatar)}
                  alt={creator || "creator avatar"}
                  className="campaign-page__team-member-avatar"
                />
              ) : (
                <svg
                  t="1763521333448"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="2441"
                  width="200"
                  height="200"
                >
                  <path
                    d="M506.4704 606.0032a204.8 204.8 0 1 1 201.5232-204.8 203.5712 203.5712 0 0 1-201.5232 204.8z m0-386.4576a181.4528 181.4528 0 1 0 177.8688 181.3504 179.9168 179.9168 0 0 0-177.8688-181.4528z"
                    p-id="2442"
                  ></path>
                  <path
                    d="M506.4704 611.1232a210.2272 210.2272 0 1 1 206.6432-210.2272 208.6912 208.6912 0 0 1-206.6432 210.2272z m0-409.6a199.9872 199.9872 0 1 0 196.4032 199.3728 198.4512 198.4512 0 0 0-196.4032-199.8848z m0 386.4576a186.5728 186.5728 0 1 1 182.9888-187.0848 185.0368 185.0368 0 0 1-182.9888 186.5728z m0-362.9056a176.3328 176.3328 0 1 0 172.7488 175.8208 174.7968 174.7968 0 0 0-172.7488-176.3328z"
                    p-id="2443"
                  ></path>
                  <path
                    d="M771.7888 812.8512a11.776 11.776 0 0 1-11.264-8.3968 264.704 264.704 0 0 0-508.2112 0 11.776 11.776 0 1 1-22.528-6.8608 288.3584 288.3584 0 0 1 552.96 0 11.776 11.776 0 0 1-11.264 15.2576z"
                    p-id="2444"
                  ></path>
                  <path
                    d="M241.0496 817.9712A16.896 16.896 0 0 1 225.28 796.16a293.4784 293.4784 0 0 1 563.2 0 16.896 16.896 0 0 1-11.264 21.0944 17.2032 17.2032 0 0 1-21.0944-11.264 259.584 259.584 0 0 0-498.3808 0 16.9984 16.9984 0 0 1-16.6912 11.9808z m265.4208-222.3104A283.8528 283.8528 0 0 0 234.5984 798.72a6.656 6.656 0 1 0 12.8 3.8912 269.824 269.824 0 0 1 518.0416 0 6.9632 6.9632 0 0 0 8.2944 4.4032A6.656 6.656 0 0 0 778.24 798.72a283.8528 283.8528 0 0 0-271.7696-203.0592z"
                    p-id="2445"
                  ></path>
                </svg>
              )}
            </span>
            <span className="campaign-page__creator-name">{creator}</span>
          </div>

          {/* mobile-donate-share-show:仅在宽度小于768时显示 */}
          <div
            className="campaign-page__sticky-trigger-show"
            ref={stickyTriggerRef}
          >
            {/* Fundraising Progress */}
            <div className="campaign-page__progress-card-show">
              <div className="campaign-page__progress-header-show">
                <div className="campaign-page__progress-circle-show">
                  <svg
                    className="campaign-page__progress-svg-show"
                    viewBox="0 0 36 36"
                  >
                    <defs>
                      <linearGradient
                        id={progressGradientShowId}
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor={shareColor} />
                        <stop offset="100%" stopColor={donateDeepColor} />
                      </linearGradient>
                    </defs>
                    <path
                      className="campaign-page__progress-bg-show"
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e8e8e8"
                      strokeWidth="4.5"
                    />
                    <path
                      className="campaign-page__progress-bar-show"
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={`url(#${progressGradientShowId})`}
                      strokeWidth="4.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={`${progressPercentage}, 100`}
                    />
                  </svg>
                  <span className="campaign-page__progress-percentage-show">
                    {progressPercentage}
                    <sup>%</sup>
                  </span>
                </div>
                <div className="campaign-page__progress-info-show">
                  <div className="campaign-page__raised-show">
                    {formatCurrencyTwoDecimals(totalRaised)} raised of{" "}
                    <button
                      type="button"
                      className="campaign-page__goal-link-show"
                    >
                      {formatGoal(goal)}
                    </button>
                  </div>
                  {(topDonationSpotlight || recentDonors.length > 0) && (
                    <div className="campaign-page__recent-donor-show">
                      <button
                        type="button"
                        className="campaign-page__recent-donor-link-show"
                        onClick={() => openDonationsModal("newest")}
                      >
                        {topDonationSpotlight ? (
                          <>
                            {topDonationSpotlight.name} donated{" "}
                            {formatCurrency(topDonationSpotlight.amount)} &gt;
                          </>
                        ) : (
                          <>
                            {recentDonors[0].name} donated{" "}
                            {formatCurrency(recentDonors[0].amount)} &gt;
                          </>
                        )}
                        {/* {topDonationSpotlight && (
                          <>
                            {topDonationSpotlight.name} donated{" "}
                            {formatCurrency(topDonationSpotlight.amount)} &gt;
                          </>
                        )}
                        {!topDonationSpotlight && (
                          <>
                            {recentDonors[0].name} donated{" "}
                            {formatCurrency(recentDonors[0].amount)} &gt;
                          </>
                        )} */}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="campaign-page__actions-show">
              <button
                className="campaign-page__button-show campaign-page__button--share-show"
                onClick={handleOpenShareModal}
              >
                Share
              </button>
              <button
                className="campaign-page__button-show campaign-page__button--donate-show"
                onClick={handleCheckoutClick}
              >
                Donate
              </button>
            </div>
          </div>

          {/* Donation Protected Badge */}
          <div
            className="campaign-page__badge campaign-page__badge2"
            role="button"
            tabIndex={0}
            onClick={openGuaranteeModal}
            onKeyDown={handleGuaranteeBadgeKeyDown}
            aria-label="Learn about the HeartLink Giving Guarantee"
            aria-haspopup="dialog"
          >
            <div className="campaign-page__badge_inner">
              <span className="campaign-page__badge-icon">
                <svg
                  t="1765437122525"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="16212"
                  width="200"
                  height="200"
                >
                  <path
                    d="M934.2464 556.7488c-27.477333 181.589333-235.895467 455.099733-422.741333 455.099733S116.241067 738.338133 88.763733 556.7488h46.6432c4.829867 0 9.642667-0.682667 14.2848-2.030933 26.624 155.818667 203.4688 385.194667 361.813334 385.194666 159.163733 0 336.469333-232.106667 361.693866-387.584 6.314667 2.833067 13.329067 4.420267 20.718934 4.420267h40.328533z m3.106133-101.137067h-43.434666c-6.0416-0.017067-12.032 1.041067-17.698134 3.157334V196.317867c-171.741867 0-271.786667-50.875733-364.714666-123.904-93.422933 73.028267-193.416533 123.904-365.226667 123.904v260.437333a50.756267 50.756267 0 0 0-10.871467-1.143467H85.6576V144.930133c200.055467 0 317.269333-59.170133 425.847467-144.4352 108.0832 85.333333 225.28 144.4352 425.847466 144.4352v310.6816z"
                    fill="#1c456b"
                    p-id="16213"
                  ></path>
                  <path
                    d="M208.913067 515.976533v-266.752c142.984533 0 226.304-42.154667 303.616-102.775466 77.294933 60.672 160.597333 102.775467 303.581866 102.775466v266.752c0 126.9248-160.682667 351.4368-303.598933 351.4368-142.916267 0-303.598933-224.512-303.598933-351.4368z m439.944533-127.061333l-161.245867 158.976-86.084266-88.183467-47.035734 45.909334 132.232534 135.389866L695.125333 435.8144l-46.1312-46.830933-0.136533-0.068267z"
                    fill="#1c456b"
                    p-id="16214"
                  ></path>
                </svg>
              </span>
              <span className="campaign-page__badge-text">
                Donation protected
              </span>
            </div>
          </div>

          {/* Story Section */}
          <div className="campaign-page__story" ref={storySectionRef}>
            {displayedParagraphs.map((paragraph, index) => (
              <div
                key={`story-${index}`}
                className="campaign-page__story-paragraph"
                dangerouslySetInnerHTML={{
                  __html: (paragraph || "").replace(/<br\s*\/?>/gi, ""),
                }}
              />
            ))}
            {(hasMoreContent || isExpanded) && (
              <button
                className="campaign-page__read-more"
                onClick={() => setIsExpanded(!isExpanded)}
                type="button"
              >
                {isExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </div>

          <div className="campaign-page__organizer">
            <div className="campaign-page__organizer-header">
              <h2 className="campaign-page__organizer-title">
                {organizer?.[0]?.title || "Organizer"}
              </h2>
              <span className="campaign-page__organizer-badge">
                {organizer?.[0]?.list?.length ?? 1}
              </span>
            </div>
            <div className="campaign-page__organizer-info">
              <span className="campaign-page__organizer-icon">
                {organizerObj.img ? (
                  <img
                    src={organizerObj.img}
                    alt={organizerObj.name}
                    className="campaign-page__team-member-avatar"
                  />
                ) : (
                  <svg
                    t="1763521333448"
                    className="icon"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="2441"
                    width="200"
                    height="200"
                  >
                    <path
                      d="M506.4704 606.0032a204.8 204.8 0 1 1 201.5232-204.8 203.5712 203.5712 0 0 1-201.5232 204.8z m0-386.4576a181.4528 181.4528 0 1 0 177.8688 181.3504 179.9168 179.9168 0 0 0-177.8688-181.4528z"
                      p-id="2442"
                    ></path>
                    <path
                      d="M506.4704 611.1232a210.2272 210.2272 0 1 1 206.6432-210.2272 208.6912 208.6912 0 0 1-206.6432 210.2272z m0-409.6a199.9872 199.9872 0 1 0 196.4032 199.3728 198.4512 198.4512 0 0 0-196.4032-199.8848z m0 386.4576a186.5728 186.5728 0 1 1 182.9888-187.0848 185.0368 185.0368 0 0 1-182.9888 186.5728z m0-362.9056a176.3328 176.3328 0 1 0 172.7488 175.8208 174.7968 174.7968 0 0 0-172.7488-176.3328z"
                      p-id="2443"
                    ></path>
                    <path
                      d="M771.7888 812.8512a11.776 11.776 0 0 1-11.264-8.3968 264.704 264.704 0 0 0-508.2112 0 11.776 11.776 0 1 1-22.528-6.8608 288.3584 288.3584 0 0 1 552.96 0 11.776 11.776 0 0 1-11.264 15.2576z"
                      p-id="2444"
                    ></path>
                    <path
                      d="M241.0496 817.9712A16.896 16.896 0 0 1 225.28 796.16a293.4784 293.4784 0 0 1 563.2 0 16.896 16.896 0 0 1-11.264 21.0944 17.2032 17.2032 0 0 1-21.0944-11.264 259.584 259.584 0 0 0-498.3808 0 16.9984 16.9984 0 0 1-16.6912 11.9808z m265.4208-222.3104A283.8528 283.8528 0 0 0 234.5984 798.72a6.656 6.656 0 1 0 12.8 3.8912 269.824 269.824 0 0 1 518.0416 0 6.9632 6.9632 0 0 0 8.2944 4.4032A6.656 6.656 0 0 0 778.24 798.72a283.8528 283.8528 0 0 0-271.7696-203.0592z"
                      p-id="2445"
                    ></path>
                  </svg>
                )}
              </span>
              <div className="campaign-page__organizer-details">
                <div className="campaign-page__organizer-name">
                  {organizerObj.name}
                </div>
                <div className="campaign-page__organizer-meta campaign-page__organizer-meta-inner">
                  <span className="campaign-page__organizer-role">
                    {organizerObj.role}
                  </span>
                  <span className="campaign-page__organizer-location">
                    {organizerObj.location}
                  </span>
                  <button
                    className="campaign-page__organizer-contact"
                    type="button"
                    onClick={handleCheckoutClick}
                  >
                    Contact
                  </button>
                </div>
              </div>
            </div>
            {organizer?.[0]?.list && organizer[0].list.length > 1 && (
              <div className="campaign-page__team-members">
                {organizer[0].list.slice(1).map((member, index) => (
                  <div
                    key={`team-${index}`}
                    className="campaign-page__team-member "
                  >
                    {member.avatar ? (
                      <img
                        src={resolveUrl(member.avatar || member.img || "")}
                        alt={member.name || ""}
                        className="campaign-page__team-member-avatar"
                      />
                    ) : (
                      <span className="campaign-page__organizer-icon">
                        <svg
                          t="1763521333448"
                          className="icon"
                          viewBox="0 0 1024 1024"
                          version="1.1"
                          xmlns="http://www.w3.org/2000/svg"
                          p-id="2441"
                          width="200"
                          height="200"
                        >
                          <path
                            d="M506.4704 606.0032a204.8 204.8 0 1 1 201.5232-204.8 203.5712 203.5712 0 0 1-201.5232 204.8z m0-386.4576a181.4528 181.4528 0 1 0 177.8688 181.3504 179.9168 179.9168 0 0 0-177.8688-181.4528z"
                            p-id="2442"
                          ></path>
                          <path
                            d="M506.4704 611.1232a210.2272 210.2272 0 1 1 206.6432-210.2272 208.6912 208.6912 0 0 1-206.6432 210.2272z m0-409.6a199.9872 199.9872 0 1 0 196.4032 199.3728 198.4512 198.4512 0 0 0-196.4032-199.8848z m0 386.4576a186.5728 186.5728 0 1 1 182.9888-187.0848 185.0368 185.0368 0 0 1-182.9888 186.5728z m0-362.9056a176.3328 176.3328 0 1 0 172.7488 175.8208 174.7968 174.7968 0 0 0-172.7488-176.3328z"
                            p-id="2443"
                          ></path>
                          <path
                            d="M771.7888 812.8512a11.776 11.776 0 0 1-11.264-8.3968 264.704 264.704 0 0 0-508.2112 0 11.776 11.776 0 1 1-22.528-6.8608 288.3584 288.3584 0 0 1 552.96 0 11.776 11.776 0 0 1-11.264 15.2576z"
                            p-id="2444"
                          ></path>
                          <path
                            d="M241.0496 817.9712A16.896 16.896 0 0 1 225.28 796.16a293.4784 293.4784 0 0 1 563.2 0 16.896 16.896 0 0 1-11.264 21.0944 17.2032 17.2032 0 0 1-21.0944-11.264 259.584 259.584 0 0 0-498.3808 0 16.9984 16.9984 0 0 1-16.6912 11.9808z m265.4208-222.3104A283.8528 283.8528 0 0 0 234.5984 798.72a6.656 6.656 0 1 0 12.8 3.8912 269.824 269.824 0 0 1 518.0416 0 6.9632 6.9632 0 0 0 8.2944 4.4032A6.656 6.656 0 0 0 778.24 798.72a283.8528 283.8528 0 0 0-271.7696-203.0592z"
                            p-id="2445"
                          ></path>
                        </svg>
                      </span>
                    )}
                    <div className="campaign-page__team-member-info">
                      <div className="campaign-page__team-member-name">
                        {member.name}
                      </div>
                      <div className="campaign-page__team-member-role">
                        {member.role || ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="campaign-page__support-divider"></div>

          {supportList.length > 0 && (
            <div className="campaign-page__support">
              <div className="campaign-page__support-header">
                <h2 className="campaign-page__support-title">
                  Words of support
                </h2>
                <span className="campaign-page__support-badge">
                  {supportItems.length}
                </span>
              </div>
              <p className="campaign-page__support-subtitle">
                Please donate to share words of support.
              </p>
              <div className="campaign-page__support-list">
                {supportItems
                  .slice(0, supportMessagesToShow)
                  .map((item, index) => (
                    <div
                      key={`support-${index}`}
                      className="campaign-page__support-item"
                    >
                      {/* <span className="campaign-page__support-avatar"> */}
                      {item.avatar ? (
                        <img alt="" src={resolveUrl(item.avatar)} />
                      ) : (
                        <span className="campaign-page__support-avatar">
                          <img
                            alt=""
                            src={process.env.PUBLIC_URL + "/main/handShare.png"}
                          />
                        </span>
                      )}
                      {/* </span> */}
                      <div className="campaign-page__support-body">
                        <div className="campaign-page__support-name">
                          {item.name}
                        </div>
                        <div className="campaign-page__support-meta">
                          <span className="campaign-page__support-amount">
                            {formatCurrency(item.price)}
                          </span>
                          <span className="campaign-page__support-separator">
                            ·
                          </span>
                          <span className="campaign-page__support-time">
                            {item.time}
                          </span>
                        </div>
                        <div className="campaign-page__support-message">
                          {item.message}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              {supportMessagesToShow < supportItems.length && (
                <button
                  className="campaign-page__update-see-more"
                  onClick={() =>
                    setSupportMessagesToShow((prev) =>
                      Math.min(prev + 10, supportItems.length)
                    )
                  }
                  style={{ marginTop: "24px" }}
                >
                  Show more
                </button>
              )}
            </div>
          )}

          {updateList.length > 0 && (
            <>
              {/* Updates Section */}
              <div className="campaign-page__updates">
                <div className="campaign-page__updates-header">
                  <h2 className="campaign-page__updates-title">Updates</h2>
                  <span className="campaign-page__updates-badge">
                    {visibleUpdates.length}
                  </span>
                </div>
                {visibleUpdates.slice(0, 1).map((update, index) => (
                  <div
                    key={`update-${index}`}
                    className="campaign-page__update-item"
                  >
                    <div className="campaign-page__update-meta">
                      <div className="campaign-page__update-avatar">
                        {update.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="campaign-page__update-meta-text">
                        <div className="campaign-page__update-date">
                          {update.time}
                        </div>
                        <div className="campaign-page__update-author-info">
                          <span className="campaign-page__update-author">
                            {update.name}
                          </span>
                          <span className="campaign-page__update-separator">
                            ·
                          </span>
                          <span className="campaign-page__update-role">
                            {update.role}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="campaign-page__update-content">
                      {update.text}
                    </p>
                    {update.imgList && update.imgList.length > 0 && (
                      <div className="campaign-page__update-images">
                        {update.imgList.map((image, imgIndex) => {
                          const src = resolveUrl(image);
                          return (
                            <img
                              key={imgIndex}
                              src={src}
                              alt={`Update ${index + 1} ${imgIndex + 1}`}
                              className="campaign-page__update-image"
                              onClick={() => {
                                const normalized = (update.imgList || []).map(
                                  resolveUrl
                                );
                                setImageViewerImages(normalized);
                                setCurrentImageIndex(imgIndex);
                                setImageViewerOpen(true);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    {visibleUpdates.length > 1 && (
                      <button
                        className="campaign-page__update-see-more"
                        type="button"
                        onClick={() => setIsUpdatesModalOpen(true)}
                      >
                        See {visibleUpdates.length - 1} more
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Action Buttons (Left Side) */}
          <div className="campaign-page__actions-left">
            <button
              className="campaign-page__button campaign-page__button--share campaign-page__actions-left-share"
              onClick={handleOpenShareModal}
            >
              Share
            </button>
            <button
              className="campaign-page__button campaign-page__button--donate campaign-page__actions-left-donate"
              onClick={handleCheckoutClick}
            >
              Donate
            </button>
          </div>

          {/* Footer Meta Info */}
          <div className="campaign-page__footer-meta">
            <div className="campaign-page__footer-meta-top">
              <span className="campaign-page__footer-text">
                Created {formatCreateDateDisplay(createTime)}
              </span>
              <span className="campaign-page__footer-separator">·</span>
              <button type="button" className="campaign-page__footer-category">
                {postType}
              </button>
              <div
                className="campaign-page__badge campaign-page__badge1"
                role="button"
                tabIndex={0}
                onClick={openGuaranteeModal}
                onKeyDown={handleGuaranteeBadgeKeyDown}
                aria-label="Learn about the HeartLink Giving Guarantee"
                aria-haspopup="dialog"
              >
                <div className="campaign-page__badge_inner">
                  <span className="campaign-page__badge-icon">
                    <img
                      alt=""
                      src={process.env.PUBLIC_URL + "/main/protect.png"}
                    />
                  </span>
                  <span className="campaign-page__badge-text">
                    Donation protected
                  </span>
                </div>
              </div>
            </div>
            <div className="campaign-page__footer-divider"></div>
          </div>
        </div>

        {/* Right Section - Sidebar */}
        <div className="campaign-page__sidebar">
          <div className="campaign-page__sticky-trigger">
            {/* Fundraising Progress */}
            <div className="campaign-page__progress-card">
              <div className="campaign-page__progress-header">
                <div className="campaign-page__progress-circle">
                  <svg
                    className="campaign-page__progress-svg"
                    viewBox="0 0 36 36"
                  >
                    <defs>
                      <linearGradient
                        id={progressGradientId}
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor={shareColor} />
                        <stop offset="100%" stopColor={donateDeepColor} />
                      </linearGradient>
                    </defs>
                    <path
                      className="campaign-page__progress-bg"
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e8e8e8"
                      strokeWidth="4.5"
                    />
                    <path
                      className="campaign-page__progress-bar"
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={`url(#${progressGradientId})`}
                      strokeWidth="4.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={`${progressPercentage}, 100`}
                    />
                  </svg>
                  <span className="campaign-page__progress-percentage">
                    {progressPercentage}
                    <sup>%</sup>
                  </span>
                </div>
                <div className="campaign-page__progress-info">
                  <div className="campaign-page__raised">
                    {formatCurrencyTwoDecimals(totalRaised)} raised of
                  </div>
                  <div className="campaign-page__goal-donations">
                    <button
                      type="button"
                      className="campaign-page__goal-link"
                      onClick={() => {}}
                    >
                      {formatGoal(goal)}
                    </button>
                    <span className="campaign-page__separator">·</span>
                    <button
                      type="button"
                      className="campaign-page__donations-link"
                      onClick={() => openDonationsModal("newest")}
                    >
                      {donationsCount} donations
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="campaign-page__actions">
              <button
                className="campaign-page__button campaign-page__button--share"
                onClick={handleOpenShareModal}
              >
                Share
              </button>
              <button
                className="campaign-page__button campaign-page__button--donate"
                onClick={handleCheckoutClick}
              >
                Donate now
              </button>
            </div>
          </div>

          {/* donate-people */}
          {formatShortCount(donationsLast24Count) > 0 && (
            <div className="campaign-page__donate-people">
              <div className="campaign-page__donate-people-icon">
                <svg
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  p-id="5572"
                  width="200"
                >
                  <path
                    d="M928 361.6v182.4c0 19.2 12.8 32 32 32s32-12.8 32-32v-224c0-35.2-28.8-64-64-64h-224c-19.2 0-32 12.8-32 32s12.8 32 32 32h176l-272 272-204.8-204.8c-6.4-6.4-16-9.6-22.4-9.6-9.6 0-16 3.2-22.4 9.6l-316.8 316.8c-12.8 12.8-12.8 32 0 44.8 12.8 12.8 32 12.8 44.8 0l294.4-294.4 204.8 204.8c6.4 6.4 16 9.6 22.4 9.6 9.6 0 16-3.2 22.4-9.6l297.6-297.6z"
                    p-id="5573"
                  ></path>
                </svg>
              </div>
              <div className="campaign-page__donate-people-text">
                {formatShortCount(donationsLast24Count)} people have just made a
                donation
              </div>
            </div>
          )}

          {/* Recent Donors */}
          <div className="campaign-page__donors">
            <div className="campaign-page__donors-header">
              <div className="campaign-page__donors-header-left">
                {" "}
                <h2 className="campaign-page__donors-title">Donations</h2>
                <span className="campaign-page__donors-badge">
                  {donationsCount}
                </span>
              </div>
              <button
                className="campaign-page__donors-button campaign-page__donors-button--top"
                type="button"
                onClick={() => openDonationsModal("top")}
                aria-label="See top donors"
              >
                <svg
                  t="1763525664180"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="19168"
                  width="200"
                  height="200"
                >
                  <path
                    d="M908.1 353.1l-253.9-36.9L540.7 86.1c-3.1-6.3-8.2-11.4-14.5-14.5-15.8-7.8-35-1.3-42.9 14.5L369.8 316.2l-253.9 36.9c-7 1-13.4 4.3-18.3 9.3-12.3 12.7-12.1 32.9 0.6 45.3l183.7 179.1-43.4 252.9c-1.2 6.9-0.1 14.1 3.2 20.3 8.2 15.6 27.6 21.7 43.2 13.4L512 754l227.1 119.4c6.2 3.3 13.4 4.4 20.3 3.2 17.4-3 29.1-19.5 26.1-36.9l-43.4-252.9 183.7-179.1c5-4.9 8.3-11.3 9.3-18.3 2.7-17.5-9.5-33.7-27-36.3zM664.8 561.6l36.1 210.3L512 672.7 323.1 772l36.1-210.3-152.8-149L417.6 382 512 190.7 606.4 382l211.2 30.7-152.8 148.9z"
                    p-id="19169"
                    fill="#232323"
                  ></path>
                </svg>
                See top
              </button>
            </div>
            <div className="campaign-page__donors-list">
              {recentDonors.map((donor, index) => (
                <div
                  key={`donor-${index}`}
                  className="campaign-page__donor-item"
                >
                  <span className="campaign-page__donor-svg">
                    <img
                      alt=""
                      src={process.env.PUBLIC_URL + "/main/handShare.png"}
                    />
                  </span>
                  <div className="campaign-page__donor-flex">
                    <span className="campaign-page__donor-name">
                      {donor.name || "Anonymous"}
                    </span>
                    <div className="campaign-page__donor-details">
                      <span className="campaign-page__donor-amount">
                        {formatCurrency(donor.amount)}
                      </span>
                      <span className="hrt-ml-1 hrt-mr-1">·</span>
                      <span className="campaign-page__donor-time">
                        {computeDonationTime(donor)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="campaign-page__donors-actions">
              <button
                className="campaign-page__donors-button"
                type="button"
                onClick={() => openDonationsModal("newest")}
              >
                See all
              </button>
              <button
                className="campaign-page__donors-button campaign-page__donors-button-botton-top"
                type="button"
                onClick={() => openDonationsModal("top")}
              >
                <svg
                  t="1763525664180"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="19168"
                  width="200"
                  height="200"
                >
                  <path
                    d="M908.1 353.1l-253.9-36.9L540.7 86.1c-3.1-6.3-8.2-11.4-14.5-14.5-15.8-7.8-35-1.3-42.9 14.5L369.8 316.2l-253.9 36.9c-7 1-13.4 4.3-18.3 9.3-12.3 12.7-12.1 32.9 0.6 45.3l183.7 179.1-43.4 252.9c-1.2 6.9-0.1 14.1 3.2 20.3 8.2 15.6 27.6 21.7 43.2 13.4L512 754l227.1 119.4c6.2 3.3 13.4 4.4 20.3 3.2 17.4-3 29.1-19.5 26.1-36.9l-43.4-252.9 183.7-179.1c5-4.9 8.3-11.3 9.3-18.3 2.7-17.5-9.5-33.7-27-36.3zM664.8 561.6l36.1 210.3L512 672.7 323.1 772l36.1-210.3-152.8-149L417.6 382 512 190.7 606.4 382l211.2 30.7-152.8 148.9z"
                    p-id="19169"
                    fill="#232323"
                  ></path>
                </svg>
                See top
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Updates Modal */}
      {isUpdatesModalOpen && (
        <div
          className="campaign-page__updates-modal-overlay"
          onClick={() => setIsUpdatesModalOpen(false)}
        >
          <div
            className="campaign-page__updates-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="campaign-page__updates-modal-header">
              <div className="campaign-page__updates-modal-title-wrapper">
                <h2 className="campaign-page__updates-modal-title">Updates</h2>
                <span className="campaign-page__updates-modal-badge">
                  {visibleUpdates.length}
                </span>
              </div>
              <button
                className="campaign-page__updates-modal-close"
                type="button"
                onClick={() => setIsUpdatesModalOpen(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="campaign-page__updates-modal-content">
              {visibleUpdates.map((update, index) => (
                <div
                  key={`updates-modal-${index}`}
                  className="campaign-page__updates-modal-item"
                >
                  <div className="campaign-page__update-meta">
                    <div className="campaign-page__update-avatar">
                      {update.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="campaign-page__update-meta-text">
                      <div className="campaign-page__update-date">
                        {update.time}
                      </div>
                      <div className="campaign-page__update-author-info">
                        <span className="campaign-page__update-author">
                          {update.name}
                        </span>
                        <span className="campaign-page__update-separator">
                          ·
                        </span>
                        <span className="campaign-page__update-role">
                          {update.role}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="campaign-page__update-content">{update.text}</p>
                  {update.imgList && update.imgList.length > 0 && (
                    <div className="campaign-page__update-images">
                      {update.imgList.map((image, imgIndex) => {
                        const src = resolveUrl(image);
                        return (
                          <img
                            key={imgIndex}
                            src={src}
                            alt={`Update ${index + 1} ${imgIndex + 1}`}
                            className="campaign-page__update-image"
                            onClick={() => {
                              const normalized = (update.imgList || []).map(
                                resolveUrl
                              );
                              setImageViewerImages(normalized);
                              setCurrentImageIndex(imgIndex);
                              setImageViewerOpen(true);
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="campaign-page__updates-modal-actions">
              <button
                className="campaign-page__button--share campaign-page__button"
                type="button"
                onClick={() => {
                  setIsUpdatesModalOpen(false);
                  handleOpenShareModal();
                }}
              >
                Share
              </button>
              <button
                className="campaign-page__button--donate campaign-page__button"
                onClick={handleCheckoutClick}
                type="button"
              >
                Donate
              </button>
            </div>
          </div>
        </div>
      )}

      {isDonationsModalOpen && (
        <div
          className="campaign-page__donations-modal-overlay"
          onClick={closeDonationsModal}
        >
          <div
            className="campaign-page__donations-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="campaign-page__donations-modal-header">
              <div className="campaign-page__donations-modal-title">
                <h2>Donations</h2>
                <span>{currentDonations.length}</span>
              </div>
              <button
                className="campaign-page__donations-modal-close"
                type="button"
                onClick={closeDonationsModal}
                aria-label="Close donations modal"
              >
                ×
              </button>
            </div>
            <div className="campaign-page__donations-modal-tabs">
              <button
                type="button"
                className={`campaign-page__donations-modal-tab${
                  donationsView === "newest"
                    ? " campaign-page__donations-modal-tab--active"
                    : ""
                }`}
                onClick={() => setDonationsView("newest")}
              >
                Newest
              </button>
              <button
                type="button"
                className={`campaign-page__donations-modal-tab${
                  donationsView === "top"
                    ? " campaign-page__donations-modal-tab--active"
                    : ""
                }`}
                onClick={() => setDonationsView("top")}
              >
                Top
              </button>
            </div>
            <div className="campaign-page__donations-modal-content">
              {donationsView === "top" && (
                <p className="campaign-page__top-donor-message">
                  Be a top donor for {creator} with ${goal / 100} or more.
                </p>
              )}
              {donationRows}
              <p className="campaign-page__donations-modal-end">
                That’s all the donors.{" "}
                <button type="button" onClick={closeDonationsModal}>
                  Close
                </button>
              </p>
            </div>
            <button
              type="button"
              className="campaign-page__donations-modal-donate"
              onClick={handleCheckoutClick}
            >
              Donate
            </button>
          </div>
        </div>
      )}

      {isGuaranteeModalOpen && (
        <div
          className="campaign-page__guarantee-modal-overlay"
          onClick={closeGuaranteeModal}
        >
          <div
            className="campaign-page__guarantee-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="campaign-giving-guarantee-title"
          >
            <button
              type="button"
              className="campaign-page__guarantee-close"
              onClick={closeGuaranteeModal}
              aria-label="Close Giving Guarantee modal"
            >
              ×
            </button>
            <div className="campaign-page__guarantee-icon">
              <img alt="" src={process.env.PUBLIC_URL + "/head/shield.svg"} />
            </div>
            <h2
              id="campaign-giving-guarantee-title"
              className="campaign-page__guarantee-title"
            >
              HeartLink Giving Guarantee
            </h2>
            <p className="campaign-page__guarantee-subtitle">
              We guarantee you a full refund in the rare case something isn’t
              right.{" "}
            </p>
            <div className="campaign-page__guarantee-divider" />
            <ul className="campaign-page__guarantee-list">
              <li>
                <span className="campaign-page__guarantee-bullet">
                  <svg
                    t="1764229468851"
                    class="icon"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="6800"
                    width="200"
                    height="200"
                  >
                    <path
                      d="M512 972.8c254.498133 0 460.8-206.301867 460.8-460.8S766.498133 51.2 512 51.2 51.2 257.501867 51.2 512s206.301867 460.8 460.8 460.8z m0-68.266667C295.202133 904.533333 119.466667 728.797867 119.466667 512S295.202133 119.466667 512 119.466667s392.533333 175.735467 392.533333 392.533333-175.735467 392.533333-392.533333 392.533333z"
                      fill="#000000"
                      p-id="6801"
                    ></path>
                    <path
                      d="M374.016 597.333333a34.133333 34.133333 0 1 0 49.271467 47.274667l104.516266-108.987733A34.133333 34.133333 0 0 0 537.2928 512V266.410667a34.133333 34.133333 0 1 0-68.266667 0v231.867733l-95.010133 99.072z"
                      fill="#000000"
                      p-id="6802"
                    ></path>
                  </svg>
                </span>
                <span>
                  Donations of any amount are covered for one full year after
                  you donate.
                </span>
              </li>
              <li>
                <span className="campaign-page__guarantee-bullet">
                  <svg
                    t="1764229628681"
                    class="icon"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="7930"
                    width="200"
                    height="200"
                  >
                    <path
                      d="M130.346667 554.666667c13.333333 120.64 82.56 224.426667 181.184 284.928-42.773333-75.392-70.101333-175.146667-75.776-284.928H130.346667z m0-85.333334h105.408c5.674667-109.781333 33.002667-209.536 75.776-284.928C212.906667 244.906667 143.68 348.693333 130.346667 469.333333z m763.306666 0c-13.333333-120.64-82.56-224.426667-181.184-284.928 42.773333 75.392 70.101333 175.146667 75.776 284.928h105.408z m0 85.333334h-105.386666c-5.674667 109.781333-33.002667 209.536-75.776 284.928C811.093333 779.093333 880.32 675.306667 893.653333 554.666667zM469.333333 885.632V554.666667h-148.096c9.493333 164.074667 72.938667 294.293333 148.096 330.965333zM321.237333 469.333333H469.333333V138.368C394.176 175.04 330.730667 305.258667 321.237333 469.333333zM554.666667 138.368V469.333333h148.096C693.269333 305.258667 629.824 175.04 554.666667 138.368zM702.762667 554.666667H554.666667v330.965333c75.157333-36.672 138.602667-166.890667 148.096-330.965333zM512 981.333333C252.8 981.333333 42.666667 771.2 42.666667 512S252.8 42.666667 512 42.666667s469.333333 210.133333 469.333333 469.333333-210.133333 469.333333-469.333333 469.333333z"
                      p-id="7931"
                      fill="#000000"
                    ></path>
                  </svg>
                </span>
                <span>Worldwide coverage, wherever you donate from.</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Quick Share Modal */}
      <QuickShareModal
        isOpen={isQuickShareModalOpen}
        onClose={() => setIsQuickShareModalOpen(false)}
        campaignUrl={campaignUrl}
        detailsData={detailsData}
      />

      {/* Image Viewer Modal */}
      {imageViewerOpen && imageViewerImages.length > 0 && (
        <div
          className="campaign-page__image-viewer-overlay"
          onClick={() => setImageViewerOpen(false)}
        >
          <div
            className="campaign-page__image-viewer"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="campaign-page__image-viewer-close"
              onClick={() => setImageViewerOpen(false)}
              aria-label="Close image viewer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="24"
                height="24"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {imageViewerImages.length > 1 && (
              <>
                <button
                  className="campaign-page__image-viewer-nav campaign-page__image-viewer-nav--prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(
                      (prev) =>
                        (prev - 1 + imageViewerImages.length) %
                        imageViewerImages.length
                    );
                  }}
                  aria-label="Previous image"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="24"
                    height="24"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <button
                  className="campaign-page__image-viewer-nav campaign-page__image-viewer-nav--next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(
                      (prev) => (prev + 1) % imageViewerImages.length
                    );
                  }}
                  aria-label="Next image"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="24"
                    height="24"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </>
            )}

            <div className="campaign-page__image-viewer-content">
              <img
                src={imageViewerImages[currentImageIndex]}
                alt={`${currentImageIndex + 1} of ${imageViewerImages.length}`}
                className="campaign-page__image-viewer-img"
              />
            </div>

            {imageViewerImages.length > 1 && (
              <div className="campaign-page__image-viewer-counter">
                {currentImageIndex + 1} / {imageViewerImages.length}
              </div>
            )}

            {imageViewerImages.length > 1 && (
              <div className="campaign-page__image-viewer-thumbnails">
                {imageViewerImages.map((image, index) => (
                  <img
                    key={`thumb-${index}`}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className={`campaign-page__image-viewer-thumbnail ${
                      index === currentImageIndex
                        ? "campaign-page__image-viewer-thumbnail--active"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reactions Modal */}
      {isReactionsModalOpen && (
        <div
          className="campaign-page__reactions-modal-overlay"
          onClick={() => setIsReactionsModalOpen(false)}
        >
          <div
            className="campaign-page__reactions-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="campaign-page__reactions-modal-header">
              <div className="campaign-page__reactions-modal-title-wrapper">
                <h2 className="campaign-page__reactions-modal-title">
                  {reactions.length} reactions
                </h2>
                <p className="campaign-page__reactions-modal-subtitle">
                  Join the community and show your support on this GoFundMe.
                </p>
              </div>
              <button
                className="campaign-page__reactions-modal-close"
                type="button"
                onClick={() => setIsReactionsModalOpen(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="campaign-page__reactions-modal-content">
              <ul className="campaign-page__reactions-list">
                {reactions.map((reaction, index) => (
                  <li
                    key={`reaction-${index}`}
                    className="campaign-page__reactions-list-item"
                  >
                    <div className="campaign-page__reactions-avatar-wrapper">
                      <div className="campaign-page__reactions-avatar">
                        <svg
                          t="1763521333448"
                          className="icon"
                          viewBox="0 0 1024 1024"
                          version="1.1"
                          xmlns="http://www.w3.org/2000/svg"
                          p-id="2441"
                          width="200"
                          height="200"
                        >
                          <path
                            d="M506.4704 606.0032a204.8 204.8 0 1 1 201.5232-204.8 203.5712 203.5712 0 0 1-201.5232 204.8z m0-386.4576a181.4528 181.4528 0 1 0 177.8688 181.3504 179.9168 179.9168 0 0 0-177.8688-181.4528z"
                            p-id="2442"
                          ></path>
                          <path
                            d="M506.4704 611.1232a210.2272 210.2272 0 1 1 206.6432-210.2272 208.6912 208.6912 0 0 1-206.6432 210.2272z m0-409.6a199.9872 199.9872 0 1 0 196.4032 199.3728 198.4512 198.4512 0 0 0-196.4032-199.8848z m0 386.4576a186.5728 186.5728 0 1 1 182.9888-187.0848 185.0368 185.0368 0 0 1-182.9888 186.5728z m0-362.9056a176.3328 176.3328 0 1 0 172.7488 175.8208 174.7968 174.7968 0 0 0-172.7488-176.3328z"
                            p-id="2443"
                          ></path>
                          <path
                            d="M771.7888 812.8512a11.776 11.776 0 0 1-11.264-8.3968 264.704 264.704 0 0 0-508.2112 0 11.776 11.776 0 1 1-22.528-6.8608 288.3584 288.3584 0 0 1 552.96 0 11.776 11.776 0 0 1-11.264 15.2576z"
                            p-id="2444"
                          ></path>
                          <path
                            d="M241.0496 817.9712A16.896 16.896 0 0 1 225.28 796.16a293.4784 293.4784 0 0 1 563.2 0 16.896 16.896 0 0 1-11.264 21.0944 17.2032 17.2032 0 0 1-21.0944-11.264 259.584 259.584 0 0 0-498.3808 0 16.9984 16.9984 0 0 1-16.6912 11.9808z m265.4208-222.3104A283.8528 283.8528 0 0 0 234.5984 798.72a6.656 6.656 0 1 0 12.8 3.8912 269.824 269.824 0 0 1 518.0416 0 6.9632 6.9632 0 0 0 8.2944 4.4032A6.656 6.656 0 0 0 778.24 798.72a283.8528 283.8528 0 0 0-271.7696-203.0592z"
                            p-id="2445"
                          ></path>
                        </svg>
                      </div>
                      <div className="campaign-page__reactions-emoji">
                        <span
                          aria-label="Reaction"
                          role="img"
                          style={{ fontSize: "1rem" }}
                        >
                          {reaction.icons}
                        </span>
                      </div>
                    </div>
                    <div className="campaign-page__reactions-name">
                      {reaction.name}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Footer - Mobile Only */}
      {isStickyFooterVisible && !isAnyModalOpen && (
        <div className="campaign-page__sticky-footer">
          <div className="campaign-page__sticky-footer-content">
            <div className="campaign-page__sticky-progress">
              <div className="campaign-page__sticky-progress-circle">
                <svg
                  className="campaign-page__progress-svg-show"
                  viewBox="0 0 36 36"
                >
                  <defs>
                    <linearGradient
                      id={progressGradientShowId}
                      x1="0%"
                      y1="100%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor={shareColor} />
                      <stop offset="100%" stopColor={donateDeepColor} />
                    </linearGradient>
                  </defs>
                  <path
                    className="campaign-page__progress-bg-show"
                    d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e8e8e8"
                    strokeWidth="4.5"
                  />
                  <path
                    className="campaign-page__progress-bar-show"
                    d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={`url(#${progressGradientShowId})`}
                    strokeWidth="4.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray={`${progressPercentage}, 100`}
                  />
                </svg>
                <span className="campaign-page__sticky-progress-percentage">
                  {progressPercentage}
                  <sup>%</sup>
                </span>
              </div>
              <div className="campaign-page__sticky-progress-info">
                <div className="campaign-page__sticky-raised">
                  {formatCurrencyTwoDecimals(totalRaised)} raised of{" "}
                  <button
                    type="button"
                    className="campaign-page__sticky-goal-link"
                    onClick={() => {}}
                  >
                    {formatGoal(goal)}
                  </button>
                </div>
                {(topDonationSpotlight || recentDonors.length > 0) && (
                  <div className="campaign-page__recent-donor">
                    <button
                      type="button"
                      className="campaign-page__recent-donor-link"
                      onClick={() => openDonationsModal("newest")}
                    >
                      {/* {topDonationSpotlight && (
                        <>
                          {topDonationSpotlight.name} donated{" "}
                          {formatCurrency(topDonationSpotlight.amount)} &gt;
                        </>
                      )}
                      {!topDonationSpotlight && (
                        <>
                          {recentDonors[0].name} donated{" "}
                          {formatCurrency(recentDonors[0].amount)} &gt;
                        </>
                      )} */}

                      {topDonationSpotlight ? (
                        <>
                          {topDonationSpotlight.name} donated{" "}
                          {formatCurrency(topDonationSpotlight.amount)} &gt;
                        </>
                      ) : (
                        <>
                          {recentDonors[0].name} donated{" "}
                          {formatCurrency(recentDonors[0].amount)} &gt;
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="campaign-page__sticky-actions">
              <button
                className="campaign-page__button campaign-page__button--share"
                onClick={handleOpenShareModal}
              >
                Share
              </button>
              <button
                className="campaign-page__button campaign-page__button--donate"
                onClick={handleCheckoutClick}
              >
                Donate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignPage;
